/**
 * 管理端应用根组件：SessionProvider（注入 transport 的 401 处理与 OTel 用户上下文订阅）+ AppRouter。
 * 在 BrowserRouter 内（main.tsx 包裹），可用 useNavigate。
 * OTel 初始化在 main.tsx 顶层调用（避免测试 render App 时注册全局 provider）。
 *
 * 会话恢复：启动时从 localStorage 读取上次会话快照注入 initialSession（跳过首帧 null 重定向登录页），
 * 并在恢复后自动重拉权限快照——权限不持久化，保证权限矩阵变更后刷新即生效。
 * 令牌过期由服务端 401 兜底：Transport onUnauthorized 清空会话并重定向登录页。
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SessionProvider, useSession, type SessionState } from './auth/SessionContext';
import { loadPersistedSession } from './auth/session-storage';
import { AppRouter } from './router';
import { setSessionUser, clearSessionUser } from './observability/otel';

/** 恢复会话后重拉权限快照（独立组件消费 useSession，避免 App 与 Provider 循环依赖）。 */
function PermissionRefreshOnBoot() {
  const { session, refreshPermissions } = useSession();
  // 依赖 token 口径而非 session 对象：快照岗位回写会生成新 session 对象，
  // 依赖对象本体会在成功路径重复拉取 /auth/permissions（同 token 只应拉一次）
  const token = session?.token;
  useEffect(() => {
    if (token) void refreshPermissions();
  }, [token, refreshPermissions]);
  return null;
}

export default function App() {
  const navigate = useNavigate();
  // 首帧渲染时读取一次持久化会话（惰性初始化，App 重渲染不重复读存储）
  const [bootSession] = useState<SessionState | null>(() => loadPersistedSession());

  return (
    <SessionProvider
      initialSession={bootSession}
      onUnauthorized={() => navigate('/login', { replace: true })}
      onSessionChange={(s) => (s ? setSessionUser(s) : clearSessionUser())}
    >
      <PermissionRefreshOnBoot />
      <AppRouter />
    </SessionProvider>
  );
}
